/**
 * 开户协议 PDF：App.vue 预拉 enum 缓存；
 * Android >= 1.5.42、iOS >= 1.5.45 用 jumpPage(type:3)，更早版本与 H5 仍 window.open。
 */
import JsBridge from "@/utils/jsBridge";
import { getBrowser } from "@/utils/browser";
import { compareAppVersion } from "@/utils/apiHost";
import i18n from "@/locales/index";

/** Android 从该版本起支持 jumpPage type:3 打开 PDF */
const MIN_ANDROID_PDF_JUMP_PAGE_VERSION = "1.5.42";
/** iOS 从该版本起支持 jumpPage type:3 打开 PDF */
const MIN_IOS_PDF_JUMP_PAGE_VERSION = "1.5.45";

const TYPE_TO_ENUM_KEY = {
  11: "riskDisclosureStatement",
  2: "personalDataPrivacyPolicy",
  3: "derivativesGeneralKnowledgeCourse",
  4: "personalInformationCollectionStatement",
  5: "clientStandingAuthority"
};

/** type → 多语言标题 key（与协议声明页一致） */
const TYPE_TO_TITLE_I18N_KEY = {
  1: "agreementDeclaration.a000002",
  11: "agreementDeclaration.a000020",
  2: "agreementDeclaration.a000003",
  3: "agreementDeclaration.a000004",
  4: "agreementDeclaration.a000005",
  5: "agreementDeclaration.a000006"
};

const ALL_ENUM_KEYS = [
  "riskDisclosureStatement",
  "securitiesAndFuturesClientAgreementChinese",
  "securitiesAndFuturesClientAgreementEnglish",
  "personalDataPrivacyPolicy",
  "derivativesGeneralKnowledgeCourse",
  "personalInformationCollectionStatement",
  "clientStandingAuthority"
].join(",");

/** key -> 原始 pdf url */
const pdfUrlCache = Object.create(null);
let preloadPromise = null;

/** 证券及/或期货客户协议：英文用 Eng，其余语种用中文版 */
export function resolveAgreementEnumKey(type, lang) {
  const t = Number(type);
  if (t === 1) {
    return lang === "en"
      ? "securitiesAndFuturesClientAgreementEnglish"
      : "securitiesAndFuturesClientAgreementChinese";
  }
  return TYPE_TO_ENUM_KEY[t] || "";
}

export function getLocalLang() {
  return localStorage.getItem("localLang") || "zh-CN";
}

export function resolveAgreementTitle(type, title) {
  if (title) {
    return String(title);
  }
  const i18nKey = TYPE_TO_TITLE_I18N_KEY[Number(type)];
  return i18nKey ? String(i18n.t(i18nKey)) : "";
}

function canUseAppPdfJumpPage(browser) {
  if (!browser || !browser.appVersion) return false;
  const minVersion = browser.isAndroidApp
    ? MIN_ANDROID_PDF_JUMP_PAGE_VERSION
    : MIN_IOS_PDF_JUMP_PAGE_VERSION;
  return compareAppVersion(browser.appVersion, minVersion) >= 0;
}

/**
 * 仅对 URL 路径文件名中的非 ASCII 字符做编码（修复 iOS 17 及以下 WebKit 不接受中文路径）
 * 目录、query、hash、已有百分号编码及 ASCII 字节均保持原样，避免破坏 CDN 签名路径
 * 按 Unicode code point 处理，避免 emoji 等非 BMP 字符被拆成代理项后 encode 抛错
 */
export function encodeUrlFilename(rawUrl) {
  if (!rawUrl) return rawUrl;

  const qIndex = rawUrl.indexOf("?");
  const hIndex = rawUrl.indexOf("#");
  let pathEnd = rawUrl.length;
  if (qIndex !== -1) pathEnd = Math.min(pathEnd, qIndex);
  if (hIndex !== -1) pathEnd = Math.min(pathEnd, hIndex);

  const pathPart = rawUrl.slice(0, pathEnd);
  const rest = rawUrl.slice(pathEnd);
  const slash = pathPart.lastIndexOf("/");
  if (slash === -1) return rawUrl;

  const prefix = pathPart.slice(0, slash + 1);
  const filename = pathPart.slice(slash + 1);

  let encodedFilename = "";
  // for...of 按 code point 迭代，不会把 emoji 拆成代理项
  for (const ch of filename) {
    const cp = ch.codePointAt(0);
    encodedFilename += cp < 128 ? ch : encodeURIComponent(ch);
  }

  return prefix + encodedFilename + rest;
}

/** App.vue 全局预拉全部协议 PDF 地址 */
export function preloadAgreementPdfs(apis) {
  if (!apis || !apis.getEnum) return Promise.resolve();
  if (Object.keys(pdfUrlCache).length >= ALL_ENUM_KEYS.split(",").length) {
    return Promise.resolve(pdfUrlCache);
  }
  if (preloadPromise) return preloadPromise;

  preloadPromise = apis
    .getEnum({ key: ALL_ENUM_KEYS })
    .then(res => {
      if (Array.isArray(res)) {
        res.forEach(item => {
          if (item && item.key && item.value) {
            pdfUrlCache[item.key] = item.value;
          }
        });
      }
      return pdfUrlCache;
    })
    .catch(err => {
      preloadPromise = null;
      throw err;
    });

  return preloadPromise;
}

/**
 * 打开协议 PDF（依赖 App.vue 预拉缓存）
 * @param {number|string} type 协议 type
 * @param {string} [title] 可选标题；不传则按 type 取多语言名称
 */
export function openAgreementPdf(type, title) {
  const key = resolveAgreementEnumKey(type, getLocalLang());
  const url = key && pdfUrlCache[key];
  if (!url) return;

  // iOS 17 及以下 WebKit 不接受路径中的中文，仅对文件名做 URL 编码
  const encodedUrl = encodeUrlFilename(url);
  const sep = encodedUrl.includes("?") ? "&" : "?";
  const pdfUrl = `${encodedUrl}${sep}t=${Date.now()}`;
  const pageTitle = resolveAgreementTitle(type, title);
  const browser = getBrowser();

  if (browser.isApp && canUseAppPdfJumpPage(browser)) {
    JsBridge.callHandler("jumpPage", {
      type: 3,
      title: pageTitle,
      url: pdfUrl
    });
    return;
  }

  window.open(pdfUrl, "_blank");
}
